import React, { useState, useMemo, useEffect } from 'react';
import CalcShell from '../CalcShell';

// Rounding helpers — round to nearest N minutes
function roundTo(mins, step) {
  if (!step) return mins;
  return Math.round(mins / step) * step;
}

const STORAGE_KEY = 'calc-timecard-v1';

export default function TimeCardCalc() {
  const defaultRows = () => Array.from({ length: 7 }, (_, i) => ({
    day: ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][i],
    start: '09:00', end: '17:00', brk: 30, note: ''
  }));

  const [rows, setRows] = useState(defaultRows);
  const [reportHeader, setReportHeader] = useState('Weekly Time Card');
  const [reportNotes, setReportNotes] = useState('');
  const [rate, setRate] = useState(20);
  const [otMode, setOtMode] = useState('weekly40'); // otMode radio
  const [otMultiplier, setOtMultiplier] = useState(1.5);
  const [showBlanks, setShowBlanks] = useState(true);
  const [includePay, setIncludePay] = useState(true);
  const [roundStep, setRoundStep] = useState(0); // in minutes
  const [savedFlash, setSavedFlash] = useState('');

  // Load from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (s.rows) setRows(s.rows);
        if (s.rate != null) setRate(s.rate);
        if (s.otMultiplier != null) setOtMultiplier(s.otMultiplier);
        if (s.otMode) setOtMode(s.otMode);
        if (s.roundStep != null) setRoundStep(s.roundStep);
        if (s.reportHeader) setReportHeader(s.reportHeader);
        if (s.reportNotes) setReportNotes(s.reportNotes);
        if (s.includePay != null) setIncludePay(s.includePay);
      }
    } catch {}
  }, []);

  const save = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ rows, rate, otMultiplier, otMode, roundStep, reportHeader, reportNotes, includePay }));
      setSavedFlash('Saved to browser'); setTimeout(() => setSavedFlash(''), 1500);
    } catch { setSavedFlash('Save failed'); }
  };
  const clearAll = () => { setRows(defaultRows()); localStorage.removeItem(STORAGE_KEY); setSavedFlash('Cleared'); setTimeout(() => setSavedFlash(''), 1500); };

  const compute = (r) => {
    if (!r.start || !r.end) return 0;
    const [sh, sm] = r.start.split(':').map(Number);
    const [eh, em] = r.end.split(':').map(Number);
    let mins = (eh*60 + em) - (sh*60 + sm);
    if (mins < 0) mins += 24*60;
    mins -= r.brk || 0;
    mins = roundTo(Math.max(0, mins), roundStep);
    return mins / 60;
  };

  const totals = useMemo(() => rows.map(compute), [rows, roundStep]);
  const totalHours = totals.reduce((a, b) => a + b, 0);

  // Split regular vs OT based on selected OT mode
  const { regular, overtime, pay } = useMemo(() => {
    let reg = 0, ot = 0;
    // Options: 'base' (pay base rate for overtime), 'cap8' (cap 8h/day), 'cap40' (cap 40h/week),
    //         'daily8' (OT after 8h/day), 'weekly40' (OT after 40h/week)
    if (otMode === 'base') {
      reg = totalHours; ot = 0;
    } else if (otMode === 'cap8') {
      totals.forEach(h => { reg += Math.min(h, 8); });
    } else if (otMode === 'cap40') {
      reg = Math.min(totalHours, 40);
    } else if (otMode === 'daily8') {
      totals.forEach(h => { if (h > 8) { reg += 8; ot += h - 8; } else reg += h; });
    } else { // weekly40
      reg = Math.min(totalHours, 40);
      ot = Math.max(0, totalHours - 40);
    }
    const mult = otMode === 'base' ? 1 : Number(otMultiplier);
    return { regular: reg, overtime: ot, pay: reg * Number(rate) + ot * Number(rate) * mult };
  }, [totals, totalHours, otMode, otMultiplier, rate]);

  const upd = (i, k, v) => { const n = [...rows]; n[i] = { ...n[i], [k]: v }; setRows(n); };
  const clearRow = (i) => upd(i, 'start', '') || upd(i, 'end', '');

  const printReport = () => window.print();

  return (
    <CalcShell look="cnet" title="Time Card Calculator" subtitle="Weekly work-hours tracker with rounding, breaks, base pay, overtime, notes, save-to-browser and printable report.">
      {/* Report + Settings */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="field"><label>Report Header</label><input value={reportHeader} onChange={e => setReportHeader(e.target.value)} data-testid="tc-header"/></div>
        <div className="field"><label>Base Pay Rate ($/hr)</label><input type="number" step="0.01" value={rate} onChange={e => setRate(+e.target.value || 0)} data-testid="tc-rate"/></div>
        <div className="field"><label>Round Times to (min)</label>
          <select value={roundStep} onChange={e => setRoundStep(+e.target.value)}>
            <option value={0}>Exact</option><option value={5}>5</option><option value={10}>10</option><option value={15}>15</option><option value={30}>30</option>
          </select>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={includePay} onChange={e => setIncludePay(e.target.checked)} data-testid="tc-inc-pay"/> Include payment information</label>

      {includePay && (
        <div className="p-3 rounded-xl border border-slate-200 bg-slate-50 mb-4">
          <div className="text-sm font-semibold mb-2">Overtime Pay</div>
          <div className="space-y-1 text-sm">
            {[
              ['base',      'No overtime rate — pay base rate for overtime worked'],
              ['cap8',      'No overtime pay — pay max 8 hours per day'],
              ['cap40',     'No overtime pay — pay max 40 hours per week'],
              ['daily8',    'Overtime pay after 8 hours per day'],
              ['weekly40',  'Overtime pay after 40 hours per week']
            ].map(([v, l]) => (
              <label key={v} className="flex items-center gap-2 flex-wrap">
                <input type="radio" checked={otMode === v} onChange={() => setOtMode(v)} data-testid={`tc-ot-${v}`}/>
                <span>{l}</span>
                {(v === 'daily8' || v === 'weekly40') && otMode === v && (
                  <>
                    <span className="text-slate-600 ml-2">at</span>
                    <input type="number" step="0.1" value={otMultiplier}
                      onChange={e => setOtMultiplier(+e.target.value || 0)}
                      className="w-20 px-2 py-0.5 rounded border border-slate-300 bg-white"
                      data-testid={`tc-mult-${v}`}/>
                    <span className="text-slate-600">× base rate</span>
                  </>
                )}
              </label>
            ))}
          </div>
          <div className="field mt-3"><label>Overtime Rate (× base)</label>
            <input type="number" step="0.1" value={otMultiplier} onChange={e => setOtMultiplier(+e.target.value || 0)} className="w-32" data-testid="tc-mult"/>
            <span className="text-xs text-slate-500 ml-2">e.g. 1.5 = time-and-a-half, 2.0 = double time</span>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-100">
            <th className="px-3 py-2 text-left">Day</th>
            <th className="px-3 py-2">Start</th>
            <th className="px-3 py-2">End</th>
            <th className="px-3 py-2">Break (min)</th>
            <th className="px-3 py-2 text-left">Note</th>
            <th className="px-3 py-2 text-right">Hours</th>
            <th className="px-3 py-2 text-right">Pay</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const isBlank = !r.start || !r.end;
              if (isBlank && !showBlanks) return null;
              return (
                <tr key={i} className="border-t border-slate-200" data-testid={`tc-row-${i}`}>
                  <td className="px-3 py-2 font-semibold">{r.day}</td>
                  <td className="px-3 py-2"><input type="time" value={r.start} onChange={e => upd(i, 'start', e.target.value)} className="px-2 py-1 border border-slate-200 rounded-lg"/></td>
                  <td className="px-3 py-2"><input type="time" value={r.end} onChange={e => upd(i, 'end', e.target.value)} className="px-2 py-1 border border-slate-200 rounded-lg"/></td>
                  <td className="px-3 py-2"><input type="number" value={r.brk} onChange={e => upd(i, 'brk', +e.target.value || 0)} className="w-20 px-2 py-1 border border-slate-200 rounded-lg"/></td>
                  <td className="px-3 py-2"><input value={r.note} onChange={e => upd(i, 'note', e.target.value)} placeholder="Optional…" className="w-40 px-2 py-1 border border-slate-200 rounded-lg"/></td>
                  <td className="px-3 py-2 text-right font-semibold">{totals[i].toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">${(totals[i]*rate).toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 mt-3 flex-wrap">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={showBlanks} onChange={e => setShowBlanks(e.target.checked)}/> Show blank days</label>
        <button onClick={save} className="px-3 py-1 rounded-lg bg-blue-600 text-white text-sm font-semibold" data-testid="tc-save">Save to browser</button>
        <button onClick={clearAll} className="px-3 py-1 rounded-lg bg-slate-200 text-sm font-semibold">Reset</button>
        <button onClick={printReport} className="px-3 py-1 rounded-lg border text-sm font-semibold">Print</button>
        {savedFlash && <span className="text-xs text-emerald-600">{savedFlash}</span>}
      </div>

      <div className="field mt-4"><label>Report Notes</label>
        <textarea rows={2} value={reportNotes} onChange={e => setReportNotes(e.target.value)} placeholder="Any notes for this report…"/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-6">
        <div className="result-panel"><div className="result-title">Total Hours</div><div className="result-value text-2xl" data-testid="tc-total">{totalHours.toFixed(2)}</div></div>
        <div className="result-panel"><div className="result-title">Regular</div><div className="result-value text-2xl">{regular.toFixed(2)}</div></div>
        <div className="result-panel"><div className="result-title">Overtime</div><div className="result-value text-2xl">{overtime.toFixed(2)}</div></div>
        <div className="result-panel"><div className="result-title">Gross Pay</div><div className="result-value text-2xl">${pay.toFixed(2)}</div></div>
      </div>
    </CalcShell>
  );
}
